import { ColDef, ICellRendererParams, ITooltipParams } from 'ag-grid-community';
import { ChevronRight } from '@mui/icons-material';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import { StoneXGrid, StoneXRow } from '../../../StoneX';
import { StoneXFileUploader } from '../../../StoneXMui';
import { useEffect, useState } from 'react';
import { Button, IconButton, LinearProgress, Box, Typography } from '@mui/material';
import { BudgetPricingStrategyModel, CommodityModel, GetCustomerDataResponse, GetCustomerSettingsResponse, LocationModel, CounterpartyModel, DeliveryModel, BudgetPricingComponentModel, ContractDate } from '../../../../Generated/Commodity-Management-Api';
import { Constants } from '../../../../Core/Constants';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { filter } from 'lodash';
import * as XLSX from 'xlsx';
import { useSnackbar } from "notistack";

interface UploadDeliveriesProps {
    pricingStrategies?: BudgetPricingStrategyModel[];
}

type UploadStatus = 'idle' | 'reading' | 'parsing' | 'uploading' | 'complete' | 'error';

export default function UploadDeliveries(props: UploadDeliveriesProps) {

    const { pricingStrategies } = props;

    const { enqueueSnackbar } = useSnackbar();

    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => state.commodityManagement.customerData);
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

    const [expanded, setExpanded] = useState(false);
    const [invalidCommoditiesWithRow, setInvalidCommoditiesWithRow] = useState<{ type: string; name: string; row: number }[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    
    // Progress tracking state
    const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [totalRows, setTotalRows] = useState(0);

    // Helper to parse Excel date or string date safely
    function parseDate(value: any): string | undefined {
        if (value === null || value === undefined || value === '') {
            return undefined;
        }
        
        let date: Date;
        if (typeof value === "number") {
            // Excel serial date conversion
            date = new Date(Math.round((value - 25569) * 86400 * 1000));
        } else if (typeof value === "string") {
            date = new Date(value);
        } else {
            return undefined;
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return undefined;
        }

        return date.toISOString().substring(0, 10);
    }

    // Helper to parse numeric values that may contain commas
    function parseNumber(value: any): number {
        if (value === null || value === undefined || value === '') {
            return 0;
        }
        if (typeof value === 'number') {
            return value;
        }
        // Remove commas and parse
        const cleaned = String(value).replace(/,/g, '');
        const parsed = parseFloat(cleaned);
        return isNaN(parsed) ? 0 : parsed;
    }

    // Helper to map a row to UpdateBudgetModel
    function mapRowToUpdateDeliveryModel(row: any): DeliveryModel {

        return {
            deliveryId: 0,
            postingDate: parseDate(row.postingdate),
            documentDate: parseDate(row.documentdate),
            plant: row.plant,
            movementType: row.movementtype,
            movementTypeText: row.movementtypetext,
            amountInLocation: parseNumber(row.amtinloccur || row.amountinlocation),
            reasonForMovement: row.reasonformovement,
            storageLocation: row.storagelocation,
            material: row.material,
            materialDescription: row.materialdescription,
            batch: row.batch ?? '',
            quantity: parseNumber(row.quantity),
            baseUnitofMeasure: row.baseunitofmeasure,
            customer: row.customer ?? '',
            order: row.materialdocument,
            recievingPlant: row.receivingplant || row.recievingplant || 0,
            vendor: row.vendor,
            purchaseOrder: row.purchaseorder || 0
        } as DeliveryModel;
    }

    // Helper to aggregate deliveries by order
    function aggregateDeliveriesByOrder(deliveries: DeliveryModel[]): DeliveryModel[] {
        const orderMap = new Map<string, DeliveryModel[]>();
        
        // Group deliveries by order
        deliveries.forEach(delivery => {
            const orderKey = delivery.order ?? '';
            if (!orderMap.has(orderKey)) {
                orderMap.set(orderKey, []);
            }
            orderMap.get(orderKey)!.push(delivery);
        });
        
        // Aggregate each group
        const aggregatedDeliveries: DeliveryModel[] = [];
        
        orderMap.forEach((group, orderKey) => {
            if (group.length === 1) {
                aggregatedDeliveries.push(group[0]);
            } else {
                // Find the row with the greatest quantity to use for non-summed fields
                const baseDelivery = group.reduce((max, current) => 
                    (current.quantity ?? 0) > (max.quantity ?? 0) ? current : max
                );
                
                // Sum quantity and amountInLocation
                const totalQuantity = group.reduce((sum, d) => sum + (d.quantity ?? 0), 0);
                const totalAmountInLocation = group.reduce((sum, d) => sum + (d.amountInLocation ?? 0), 0);
                
                aggregatedDeliveries.push({
                    ...baseDelivery,
                    quantity: totalQuantity,
                    amountInLocation: totalAmountInLocation
                });
            }
        });
        
        return aggregatedDeliveries;
    }

    function resetProgress() {
        setUploadStatus('idle');
        setProgress(0);
        setProgressMessage('');
        setTotalRows(0);
    }

    function getDeliveriesFromUpload(file: File) {

        resetProgress();
        setUploadStatus('reading');
        setProgressMessage(`Reading file: ${file.name}`);
        setProgress(10);

        const requiredHeaders = [
            "Posting Date",
            "Document Date",
            "Plant",
            "Movement Type",
            "Movement Type Text",
            "Amount In Location",
            "Reason For Movement",
            "Storage Location",
            "Material",
            "Material Description",
            "Batch",
            "Quantity",
            "Base Unit of Measure", 
            "Customer", 
            "Order", 
            "Recieving Plant", 
            "Vendor", 
            "Purchase Order",
            "Material Document"
        ];

        const reader = new FileReader();
        
        reader.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentLoaded = Math.round((event.loaded / event.total) * 30); // 0-30% for file reading
                setProgress(10 + percentLoaded);
            }
        };

        reader.onload = (event) => {
            setUploadStatus('parsing');
            setProgress(40);
            setProgressMessage('Parsing Excel data...',);

            const data = new Uint8Array(event.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Get headers from the first row
            const headerRow: string[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[] || [];
            // Remove '*' from requiredHeaders before comparing
            const normalizedRequiredHeaders = requiredHeaders.map(h => h.replace(/\*/g, ''));
            const normalizedHeaderRow = headerRow.map(h => h.replace(/\*/g, ''));

            // Read rows with headers
            let rows: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 0, defval: '' });
            
            setTotalRows(rows.length);
            setProgress(50);
            setProgressMessage(`Processing ${rows.length} rows...`);

            // Normalize keys for each row independently (not by index)
            rows = rows.map((row, index) => {
                const newRow: any = {};
                Object.keys(row).forEach((key) => {
                    // Normalize key: remove *, spaces, dots, and convert to lowercase
                    const normalizedKey = key
                        .replace(/\*/g, '')
                        .replace(/\s/g, '')
                        .replace(/\./g, '')
                        .toLowerCase();
                    newRow[normalizedKey] = row[key];
                });
                
                // Update progress every 100 rows
                if (index % 100 === 0) {
                    const parseProgress = 50 + Math.round((index / rows.length) * 20);
                    setProgress(parseProgress);
                }
                
                return newRow;
            });

            setProgress(70);
            setProgressMessage('Filtering rows by movement type...');

            // Only include rows with movement type 101 or 102
            rows = rows.filter(row => {
                const movementType = String(row.movementtype ?? '').trim();
                return movementType === '101' || movementType === '102';
            });

            setTotalRows(rows.length);
            setProgressMessage('Converting to delivery models...');

            const updateDeliveryModels: DeliveryModel[] = [];

            rows.forEach((row, idx) => {
                updateDeliveryModels.push(mapRowToUpdateDeliveryModel(row));
            });

            // Aggregate deliveries by order
            setProgress(75);
            setProgressMessage('Aggregating deliveries by order...');
            const aggregatedDeliveries = aggregateDeliveriesByOrder(updateDeliveryModels);

            setProgress(80);
            setProgressMessage(`Uploading ${aggregatedDeliveries.length} deliveries (aggregated from ${updateDeliveryModels.length} rows)...`);

            // Step 4: If all commodities are valid, proceed with API call
            saveDeliveries(aggregatedDeliveries);
        };

        reader.onerror = () => {
            setUploadStatus('error');
            setProgressMessage('Error reading file');
            enqueueSnackbar('Error reading file', { variant: 'error' });
        };

        reader.readAsArrayBuffer(file);
    }

    async function saveDeliveries(deliveries: DeliveryModel[]) {
        setIsSaving(true);
        setUploadStatus('uploading');
        
        const BATCH_SIZE = 500; // Adjust based on your server limits
        const batches: DeliveryModel[][] = [];
        
        // Split deliveries into batches
        for (let i = 0; i < deliveries.length; i += BATCH_SIZE) {
            batches.push(deliveries.slice(i, i + BATCH_SIZE));
        }
        
        let completedBatches = 0;
        const totalBatches = batches.length;
        
        try {
            for (const batch of batches) {
                const payload = { deliveries: batch };
                await CommodityManagementApi.updateDeliveries(undefined, payload);
                
                completedBatches++;
                const batchProgress = 80 + Math.round((completedBatches / totalBatches) * 20);
                setProgress(batchProgress);
                setProgressMessage(`Uploaded batch ${completedBatches} of ${totalBatches} (${batch.length} deliveries)`);
            }
            
            setProgress(100);
            setUploadStatus('complete');
            setProgressMessage(`Successfully uploaded ${deliveries.length} deliveries in ${totalBatches} batches`);
            enqueueSnackbar('Deliveries were successfully updated', { variant: 'success' });
            
            setTimeout(() => {
                resetProgress();
                setIsSaving(false);
            }, 3000);
            
        } catch (err) {
            setUploadStatus('error');
            setProgressMessage(`Error uploading batch ${completedBatches + 1} of ${totalBatches}`);
            enqueueSnackbar('An error occurred while saving the deliveries', { variant: 'error' });
            setIsSaving(false);
        }
    }

    const getProgressColor = () => {
        switch (uploadStatus) {
            case 'complete': return 'success';
            case 'error': return 'error';
            default: return 'primary';
        }
    };

    return (
        <div style={{ borderTop: '1px solid var(--gray5)', borderBottom: '1px solid var(--gray5)' }}>
            <StoneXRow align="center" marginDirection="topAndBottom">
                <IconButton onClick={() => setExpanded((previous) => !previous)}>
                    <ChevronRight sx={{ transform: expanded ? 'rotate(90deg)' : undefined }} />
                </IconButton>
                <h3 style={{ color: 'var(--gray2)' }}>Upload Deliveries</h3>
            </StoneXRow>

            {expanded && <>
                
                <StoneXRow>
                    <StoneXFileUploader 
                        message="Upload deliveries" 
                        onUpload={getDeliveriesFromUpload}
                    />
                </StoneXRow>

                {/* Progress Bar */}
                {uploadStatus !== 'idle' && (
                    <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {progressMessage}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {progress}%
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={progress} 
                            color={getProgressColor()}
                            sx={{ height: 8, borderRadius: 4 }}
                        />
                        {totalRows > 0 && uploadStatus !== 'complete' && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                Processing {totalRows} rows
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Table for invalid commodities with row numbers */}
                {invalidCommoditiesWithRow.length > 0 && (
                    <div style={{ margin: '16px 0' }}>
                        <h4 style={{ color: 'red' }}>Invalid Entries in Upload</h4>
                        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Type</th>
                                    <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Name</th>
                                    <th style={{ border: '1px solid #ccc', padding: '4px 8px', background: '#f8d7da', color: '#721c24' }}>Row Number</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invalidCommoditiesWithRow.map(({ type, name, row }, idx) => (
                                    <tr key={name + '_' + row + '_' + idx}>
                                        <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{type}</td>
                                        <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{name}</td>
                                        <td style={{ border: '1px solid #ccc', padding: '4px 8px' }}>{row}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>}
        </div>
    );
}
